'use client';

/**
 * Layer ownership (docs/THEME.md 2.10): every portal records the surface that owns it and renders INTO that owner.
 *
 *   owner 0 (the page)  -> `#layer-root`, popups carry `z-page-layer` (1000)
 *   a migrated modal    -> the modal's slot: the last child of its NON-scrolling content root, outside DialogBody,
 *                          so a popup is never clipped by the body's scroll, stays inside the modal's stacking context
 *                          (above the modal, below any later modal) and inside Radix's FocusScope / hideOthers.
 *   no owner            -> LEGACY: the caller keeps today's portal target (document.body) and today's z value.
 *
 * Nothing changes for code that is not inside a provider: the app root does not mount `PageLayerRoot` yet (it is
 * mounted by the gallery and by migrated route families; the app-wide mount is a later default flip, step 18).
 *
 * Popups that read the owner: Select and DropdownMenu content (Radix Portal `container`), Popover / Combobox /
 * MultiSelect (PopupPortal), InstantTooltip, PortalMenu.
 */
import * as React from 'react';

import { ensurePortalHost } from '@/design-system/primitives/ThemedPortal';

export interface LayerOwner {
  readonly kind: 'page' | 'modal';
  /** 0 for the page, the modal depth (1, 2, ...) for a modal. */
  readonly depth: number;
  /** Where popups owned by this surface render. Null until mounted. */
  readonly slot: HTMLElement | null;
  /** A modal's identity in the modal registry (lib/layers/layerStack.ts), known before it registers. */
  readonly modalKey?: symbol;
}

const LayerOwnerContext = React.createContext<LayerOwner | null>(null);

/** The surface that owns popups rendered here, or null (legacy: no owner). */
export function useLayerOwner(): LayerOwner | null {
  return React.useContext(LayerOwnerContext);
}

/** For the modal primitives (components/ui/dialog.tsx). Feature code never provides an owner itself. */
export const LayerOwnerProvider = LayerOwnerContext.Provider;

/** The z band class every OWNED popup carries (it is relative to the owner's stacking context). */
export const OWNED_POPUP_Z = 'z-page-layer';

export interface PopupLayer {
  /** The portal container: the owner's slot, or undefined for the legacy body portal. */
  container: HTMLElement | undefined;
  owned: boolean;
  /** `z-page-layer` when owned, the caller's legacy z class otherwise. */
  zClass: string;
}

/**
 * Where a popup renders and which z class it carries. `legacyZ` is the class the popup has TODAY (kept verbatim when
 * there is no owner, so unmigrated callers do not move).
 */
export function usePopupLayer(legacyZ: string): PopupLayer {
  const owner = useLayerOwner();
  const slot = owner?.slot ?? undefined;
  return slot ? { container: slot, owned: true, zClass: OWNED_POPUP_Z } : { container: undefined, owned: false, zClass: legacyZ };
}

export const PAGE_LAYER_HOST_ID = 'layer-root';

/**
 * Owner 0. Popups rendered below this provider (and not inside a migrated modal) portal into `#layer-root`, a stable
 * host appended to body once, with no `.ds` scope, no transform and no z-index of its own.
 */
export function PageLayerRoot({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setSlot(ensurePortalHost(document, { id: PAGE_LAYER_HOST_ID }));
  }, []);
  const value = React.useMemo<LayerOwner>(() => ({ kind: 'page', depth: 0, slot }), [slot]);
  return <LayerOwnerProvider value={value}>{children}</LayerOwnerProvider>;
}
